import * as React from "react";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, ArrowLeft, Check, X } from "lucide-react";

interface DualCropModalProps {
  file: File | null;
  isOpen: boolean;
  onClose: () => void;
  activeAspectRatio: number; // Current camera aspect ratio (e.g. 9/16 or 4/3)
  onCropComplete: (bgImage: { horizontal: string; vertical: string }) => void;
}

export const DualCropModal: React.FC<DualCropModalProps> = ({
  file,
  isOpen,
  onClose,
  activeAspectRatio,
  onCropComplete,
}) => {
  const [imgUrl, setImgUrl] = React.useState<string | null>(null);
  const imageRef = React.useRef<HTMLImageElement | null>(null);

  // Stepper state: 1 for active orientation, 2 for other orientation
  const [step, setStep] = React.useState<1 | 2>(1);

  // Crop states for both modes
  const [verticalCrop, setVerticalCrop] = React.useState<Crop>();
  const [verticalCompletedCrop, setVerticalCompletedCrop] = React.useState<PixelCrop>();

  const [horizontalCrop, setHorizontalCrop] = React.useState<Crop>();
  const [horizontalCompletedCrop, setHorizontalCompletedCrop] = React.useState<PixelCrop>();

  // Determine current active and secondary orientations based on activeAspectRatio
  const isActiveVertical = activeAspectRatio === 9 / 16;

  // Current orientation step labels
  const step1Type = isActiveVertical ? "vertical" : "horizontal";
  const step2Type = isActiveVertical ? "horizontal" : "vertical";

  // Create Object URL when file is supplied
  React.useEffect(() => {
    if (!file) {
      setImgUrl(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setImgUrl(url);
    setStep(1);
    // Reset crops
    setVerticalCrop(undefined);
    setVerticalCompletedCrop(undefined);
    setHorizontalCrop(undefined);
    setHorizontalCompletedCrop(undefined);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Center initial crop box once image metadata loads
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    imageRef.current = e.currentTarget;

    // Default Vertical 9:16 Crop
    const vCrop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        9 / 16,
        width,
        height
      ),
      width,
      height
    );
    setVerticalCrop(vCrop);
    setVerticalCompletedCrop({
      unit: "px",
      x: (vCrop.x * width) / 100,
      y: (vCrop.y * height) / 100,
      width: (vCrop.width * width) / 100,
      height: (vCrop.height * height) / 100,
    });

    // Default Horizontal 4:3 Crop
    const hCrop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        4 / 3,
        width,
        height
      ),
      width,
      height
    );
    setHorizontalCrop(hCrop);
    setHorizontalCompletedCrop({
      unit: "px",
      x: (hCrop.x * width) / 100,
      y: (hCrop.y * height) / 100,
      width: (hCrop.width * width) / 100,
      height: (hCrop.height * height) / 100,
    });
  };

  // Helper to programmatically draw and export crop canvas to base64 JPEG
  const generateCroppedImage = (pixelCrop: PixelCrop | undefined, aspect: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!imageRef.current || !pixelCrop) {
        reject(new Error("Image or completed crop not defined"));
        return;
      }

      const canvas = document.createElement("canvas");
      const image = imageRef.current;

      // Natural image dimensions scale factors
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Target size constrained to 1280 maximum dimension
      const maxDim = 1280;
      let targetWidth = pixelCrop.width * scaleX;
      let targetHeight = pixelCrop.height * scaleY;

      if (aspect > 1) {
        // Landscape (Horizontal 4:3)
        if (targetWidth > maxDim) {
          targetHeight = Math.round((targetHeight * maxDim) / targetWidth);
          targetWidth = maxDim;
        }
      } else {
        // Portrait (Vertical 9:16)
        if (targetHeight > maxDim) {
          targetWidth = Math.round((targetWidth * maxDim) / targetHeight);
          targetHeight = maxDim;
        }
      }

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("No 2d canvas context"));
        return;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Draw the cropped section
      ctx.drawImage(
        image,
        pixelCrop.x * scaleX,
        pixelCrop.y * scaleY,
        pixelCrop.width * scaleX,
        pixelCrop.height * scaleY,
        0,
        0,
        targetWidth,
        targetHeight
      );

      try {
        const base64 = canvas.toDataURL("image/jpeg", 0.7);
        resolve(base64);
      } catch (err) {
        reject(err);
      }
    });
  };

  const handleApply = async () => {
    try {
      // 1. Generate Vertical Crop
      const verticalBase64 = await generateCroppedImage(verticalCompletedCrop, 9 / 16);

      // 2. Generate Horizontal Crop
      const horizontalBase64 = await generateCroppedImage(horizontalCompletedCrop, 4 / 3);

      // 3. Callback with both cropped assets
      onCropComplete({
        vertical: verticalBase64,
        horizontal: horizontalBase64,
      });

      onClose();
    } catch (err) {
      console.error("Failed to generate cropped virtual backgrounds:", err);
    }
  };

  const currentCrop =
    step === 1 ? (isActiveVertical ? verticalCrop : horizontalCrop) : isActiveVertical ? horizontalCrop : verticalCrop;
  const currentAspect = step === 1 ? (isActiveVertical ? 9 / 16 : 4 / 3) : isActiveVertical ? 4 / 3 : 9 / 16;
  const currentOrientationLabel =
    step === 1
      ? isActiveVertical
        ? "Vertical (9:16)"
        : "Horizontal (4:3)"
      : isActiveVertical
      ? "Horizontal (4:3)"
      : "Vertical (9:16)";

  const setCropForCurrentStep = (c: Crop) => {
    if (step === 1) {
      if (isActiveVertical) setVerticalCrop(c);
      else setHorizontalCrop(c);
    } else {
      if (isActiveVertical) setHorizontalCrop(c);
      else setVerticalCrop(c);
    }
  };

  const setCompletedCropForCurrentStep = (pc: PixelCrop) => {
    if (step === 1) {
      if (isActiveVertical) setVerticalCompletedCrop(pc);
      else setHorizontalCompletedCrop(pc);
    } else {
      if (isActiveVertical) setHorizontalCompletedCrop(pc);
      else setVerticalCompletedCrop(pc);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose} size="lg">
      <div className="flex flex-col h-full bg-white text-zinc-800">
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="bg-teal-50 text-teal-600 p-1.5 rounded-lg">
              <Sparkles className="size-4.5" />
            </div>
            <div>
              <h2 className="text-sm font-bold leading-none">Format Virtual Background</h2>
              <p className="text-[10px] text-zinc-400 mt-0.5 font-semibold uppercase tracking-wider">
                Step {step} of 2: {currentOrientationLabel}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-50 p-1 rounded-lg transition-colors cursor-pointer"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Workspace Crop Area */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center bg-gray-50/50 min-h-[350px]">
          {imgUrl ? (
            <div className="max-h-[380px] overflow-auto flex items-center justify-center border border-gray-200/50 rounded-lg bg-zinc-900 shadow-sm p-1 max-w-full">
              <ReactCrop
                crop={currentCrop}
                aspect={currentAspect}
                onChange={c => setCropForCurrentStep(c)}
                onComplete={c => setCompletedCropForCurrentStep(c)}
                className="max-h-full"
              >
                <img
                  src={imgUrl}
                  onLoad={onImageLoad}
                  alt="Source Crop"
                  className="max-h-[340px] w-auto object-contain select-none"
                />
              </ReactCrop>
            </div>
          ) : (
            <div className="text-xs text-zinc-400 font-medium">No image loaded. Please upload a file.</div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex justify-between items-center px-6 py-4 border-t border-gray-100 shrink-0 bg-white">
          <div className="flex items-center gap-1 text-[10px] text-zinc-400 font-bold uppercase tracking-wider">
            <span className={step === 1 ? "text-teal-600 font-extrabold" : ""}>
              1. {isActiveVertical ? "Portrait" : "Landscape"}
            </span>
            <span>&bull;</span>
            <span className={step === 2 ? "text-teal-600 font-extrabold" : ""}>
              2. {isActiveVertical ? "Landscape" : "Portrait"}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {step === 2 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep(1)}
                className="flex items-center gap-1.5 text-xs font-bold text-gray-500 cursor-pointer"
              >
                <ArrowLeft className="size-3.5" /> Back
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-xs font-bold text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                Cancel
              </Button>
            )}

            {step === 1 ? (
              <Button
                size="sm"
                onClick={() => setStep(2)}
                className="flex items-center gap-1.5 text-xs font-bold bg-zinc-900 text-white cursor-pointer"
              >
                Next crop <ArrowRight className="size-3.5" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleApply}
                className="flex items-center gap-1.5 text-xs font-bold bg-teal-600 hover:bg-teal-700 text-white cursor-pointer"
              >
                Apply Backgrounds <Check className="size-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
